import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import imageCompression from 'browser-image-compression';
import { Upload, Check, Trash } from 'lucide-react';
import DashboardNavbar from './dashboardNavbar';
import { useAppContext } from '../appprovider';
import { canAccessAdmin, isOrgInstructor } from './services/instructorPortalAccess';
import {
  getVisibleSettingsTabs,
  planTabRedirectPath,
  resolveActiveSettingsTab,
} from './settings/settingsTabs';
import { getCache, setCache } from './ui/listCache';
import { planLimitsFromEntitlements } from './services/instructorMeterLimits';
import { formatPlanLabel } from './admin/Packages/planDisplayUtils';
import {
  Card,
  SectionHead,
  Btn,
  SetTabs,
  SetTab,
  SetRow,
  Switch,
  Field,
  Input,
  Textarea,
  FormGrid,
  Skeleton,
} from './ui/primitives';
import { LabelWithHelp } from './ui/FieldHelp';
import { SETTINGS_FIELD_HELP as HELP } from './ui/settingsFieldHelp';
import useDemoActionGate from './hooks/useDemoActionGate';
import DemoLockedDialog from './ui/DemoLockedDialog';
import { RegistrationSettingsCard } from './admin/register';

const LogoPreview = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: ${({ theme }) => theme.accentSoft};
  color: ${({ theme }) => theme.accentStrong};
  display: grid;
  place-items: center;
  font-family: ${({ theme }) => theme.fontHead};
  font-weight: 800;
  font-size: 20px;
  overflow: hidden;
  box-shadow: inset 0 0 0 1px ${({ theme }) => theme.accentLine};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const AvPreview = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(150deg, ${({ theme }) => theme.accent}, ${({ theme }) => theme.accentStrong});
  color: #fff;
  display: grid;
  place-items: center;
  font-family: ${({ theme }) => theme.fontHead};
  font-weight: 800;
  font-size: 18px;
`;

const readSavedPackageLimits = () => {
  try {
    const raw = localStorage.getItem('studyak_package_limits');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const buildPlanCacheKey = (uid) => `settings:plan:${uid}`;

export default function SettingsPage() {
  const navigate = useNavigate();
  const { guardAction, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { entitlements } = useAppContext();
  const visibleTabs = useMemo(
    () => getVisibleSettingsTabs(entitlements),
    [entitlements],
  );
  const requestedTab = searchParams.get('tab');
  // Sanitised against visible tabs — a ?tab=plan deep link never renders here.
  const activeTab = resolveActiveSettingsTab(entitlements, requestedTab);
  // Organisation settings are org-level: only owners/admins (and a super admin on
  // their own console) may edit and save them. Instructors get a read-only view.
  const canEditOrg = canAccessAdmin(entitlements);

  const userId = localStorage.getItem('userId');
  const email = localStorage.getItem('email');
  const apiUrl = process.env.REACT_APP_API_URL;

  // Settings has its own sub-tabs (Organisation / Profile / Account); the Admin
  // section nav (Users, Course Catalog, …) lives on the Admin pages, not here.
  const menuItems = [];

  useEffect(() => {
    // Old ?tab=plan deep links: owners land on Billing, admins on Admin → Plan & billing.
    const redirect = planTabRedirectPath(entitlements, requestedTab);
    if (redirect) {
      navigate(redirect, { replace: true });
    }
  }, [requestedTab, entitlements, navigate]);

  // --- State variables ---
  const [orgLoading, setOrgLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);
  // Setter-only: the plan tab UI moved to Admin → Plan & billing, but the fetch
  // logic stays because it primes the shared package-limits cache.
  const [, setPlanMetaLoading] = useState(true);
  const [, setPlanMetaReady] = useState(false);
  const [, setUsageLoading] = useState(false);
  const [usageReady, setUsageReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const usageFetchStarted = useRef(false);

  // Organisation settings
  const [orgName, setOrgName] = useState('StudyAK');
  const [orgLogo, setOrgLogo] = useState('');
  const [selfEnroll, setSelfEnroll] = useState(true);
  const [certificates, setCertificates] = useState(false);

  // Profile settings
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [profEmail, setProfEmail] = useState('');

  // Plan & billing settings (setter-only — display moved to Admin → Plan & billing)
  const [, setPlanName] = useState('Trial');
  const [, setPlanPrice] = useState(0);
  const [, setRenewsDate] = useState('');

  // Keep the plan name in sync with the real entitlement tier (inherited from the
  // org for invited members), even if entitlements resolve after the plan fetch ran.
  useEffect(() => {
    if (entitlements?.tier) {
      setPlanName(formatPlanLabel(entitlements.tier));
    }
  }, [entitlements?.tier]);
  const [usage, setUsage] = useState({
    courses: { used: 0, total: 1 },
    assessments: { used: 0, total: 1 },
    students: { used: 0, total: 1 },
  });


  const getBasicAuthHeader = () => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const encodedCredentials = btoa(`${username}:${password}`);
    return `Basic ${encodedCredentials}`;
  };

  const setTab = (key) => {
    setSearchParams({ tab: key });
  };

  const tabLoading = {
    org: orgLoading,
    profile: profileLoading,
    account: false,
    register: false,
  }[activeTab];

  const applyPlanMeta = (data) => {
    if (!data) return '1';
    const pkgId = data.planId || '1';
    // Name reflects the real entitlement tier (inherited from the org for invited
    // members); fall back to the legacy Trial/Professional guess only if unknown.
    setPlanName(entitlements?.tier
      ? formatPlanLabel(entitlements.tier)
      : (data.subscriptionId ? 'Professional' : 'Trial'));
    setPlanPrice(data.price || 0);
    if (data.nextPlanDate) {
      const d = new Date(data.nextPlanDate);
      setRenewsDate(d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }));
    } else {
      setRenewsDate('');
    }
    return pkgId;
  };

  const applyUsageLimits = (limits) => {
    if (!limits) return;
    setUsage((prev) => ({
      courses: { ...prev.courses, total: limits.courses || prev.courses.total },
      assessments: { ...prev.assessments, total: limits.assessments || prev.assessments.total },
      students: { ...prev.students, total: limits.students || prev.students.total },
    }));
  };

  const fetchPlanUsage = async (pkgId) => {
    if (!userId || !apiUrl || usageFetchStarted.current || usageReady) return;
    usageFetchStarted.current = true;
    setUsageLoading(true);

    try {
      const auth = { headers: { Authorization: getBasicAuthHeader() } };
      const cachedAssessments = getCache('assessments');

      const entitlementLimits = entitlements?.meters
        ? planLimitsFromEntitlements(entitlements)
        : null;
      const savedLimits = readSavedPackageLimits();
      const limits = entitlementLimits || savedLimits || { courses: 3, assessments: 15, students: 25 };

      const [coursesRes, assessRes, studentsRes] = await Promise.all([
        axios.get(`${apiUrl}/api/course/${userId}`, auth),
        cachedAssessments
          ? Promise.resolve({ data: cachedAssessments })
          : axios.get(`${apiUrl}/api/Assessment/${userId}`, auth),
        axios.get(`${apiUrl}/api/studentcourses/${userId}`, auth),
      ]);

      // Legacy limits API (commented out — use instructor_plan_entitlement via entitlements):
      // axios.get(`${apiUrl}/api/packages/${pkgId}/${userId}`, auth),

      // An explicit null limit means "unlimited" (org tier) — keep it null so the UI
      // renders "Unlimited" instead of collapsing to 1 via `|| 1`.
      const resolveLimit = (numberField, legacyField) => {
        const raw = numberField !== undefined ? numberField : legacyField;
        if (raw === null) return null; // unlimited
        if (raw === undefined) return 1; // no data → default
        return raw;
      };
      const usageData = {
        courses: { used: coursesRes.data?.length || 0, total: resolveLimit(limits.numberOfCourses, limits.courses) },
        assessments: {
          used: Array.isArray(assessRes.data) ? assessRes.data.length : 0,
          total: resolveLimit(limits.numberOfAssessments, limits.assessments),
        },
        students: { used: studentsRes.data?.length || 0, total: resolveLimit(limits.numberOfStudents, limits.students) },
      };

      setUsage(usageData);
      setUsageReady(true);

      try {
        localStorage.setItem('studyak_package_limits', JSON.stringify({
          courses: limits.numberOfCourses ?? limits.courses,
          students: limits.numberOfStudents ?? limits.students,
          assessments: limits.numberOfAssessments ?? limits.assessments,
          name: limits.name || limits.tier || '',
        }));
      } catch (storageErr) {
        // ignore storage errors
      }

      const cacheKey = buildPlanCacheKey(userId);
      const cached = getCache(cacheKey) || {};
      setCache(cacheKey, { ...cached, usage: usageData });
    } catch (error) {
      console.error('Error fetching plan usage:', error);
      usageFetchStarted.current = false;
    } finally {
      setUsageLoading(false);
    }
  };

  const prefetchPlanMeta = async () => {
    if (!userId || !apiUrl) {
      setPlanMetaLoading(false);
      return;
    }

    const cacheKey = buildPlanCacheKey(userId);
    const cached = getCache(cacheKey);
    if (cached?.meta) {
      applyPlanMeta(cached.meta.raw);
      setPlanMetaReady(true);
      setPlanMetaLoading(false);
      if (cached.usage) {
        setUsage(cached.usage);
        setUsageReady(true);
        return;
      }
      fetchPlanUsage(cached.meta.pkgId);
      return;
    }

    applyUsageLimits(readSavedPackageLimits());

    try {
      const planRes = await axios.get(`${apiUrl}/api/userplans/${userId}`, {
        headers: { Authorization: getBasicAuthHeader() },
      });
      if (planRes.data) {
        const pkgId = applyPlanMeta(planRes.data);
        setPlanMetaReady(true);
        setCache(cacheKey, {
          meta: { raw: planRes.data, pkgId },
          usage: null,
        });
        fetchPlanUsage(pkgId);
      }
    } catch (error) {
      console.error('Error fetching plan meta:', error);
    } finally {
      setPlanMetaLoading(false);
    }
  };

  const fetchOrg = async () => {
    try {
      const orgRes = await axios.get(`${apiUrl}/api/organization/${userId}`, {
        headers: { Authorization: getBasicAuthHeader() },
      });
      if (orgRes.data) {
        setOrgName(orgRes.data.orgName || '');
        if (orgRes.data.orgLogo) {
          setOrgLogo(`data:image/jpeg;base64,${orgRes.data.orgLogo}`);
        }
      }
    } catch (error) {
      console.error('Error fetching organisation settings:', error);
    } finally {
      setOrgLoading(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const profRes = await axios.get(`${apiUrl}/api/users/instructor/${userId}`, {
        headers: { Authorization: getBasicAuthHeader() },
      });
      if (profRes.data) {
        setFirstName(profRes.data.firstName || '');
        setLastName(profRes.data.lastName || '');
        setProfEmail(profRes.data.email || '');
      }
    } catch (error) {
      console.error('Error fetching profile settings:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    if (!userId || !apiUrl) return;
    fetchOrg();
    fetchProfile();
    prefetchPlanMeta();
  }, [apiUrl, userId]);

  useEffect(() => {
    if (!entitlements?.meters) return;
    const mapped = planLimitsFromEntitlements(entitlements);
    applyUsageLimits({
      courses: mapped.numberOfCourses,
      assessments: mapped.numberOfAssessments,
      students: mapped.numberOfStudents,
    });
  }, [entitlements]);

  // --- Action Handlers ---
  const handleLogoUpload = async (e) => {
    if (!guardAction('action.settings.org.uploadLogo')) {
      e.target.value = '';
      return;
    }
    const file = e.target.files[0];
    if (file) {
      try {
        let compressedFile = file;
        const options = { maxSizeMB: 0.03, maxWidthOrHeight: 300, useWebWorker: true };
        while (compressedFile.size > 30 * 1024) {
          compressedFile = await imageCompression(compressedFile, options);
        }
        const reader = new FileReader();
        reader.onloadend = () => {
          setOrgLogo(reader.result);
        };
        reader.readAsDataURL(compressedFile);
      } catch (err) {
        console.error('Error compressing image:', err);
      }
    }
  };

  const handleSaveOrg = async () => {
    if (!canEditOrg) return; // instructors get a read-only org view
    if (!guardAction('action.settings.org.save')) return;
    setSaving(true);
    const logoData = orgLogo.includes('base64,') ? orgLogo.split(',')[1] : orgLogo;
    try {
      await axios.post(
        `${apiUrl}/api/organization`,
        { userId, orgName, orgLogo: logoData },
        { headers: { Authorization: getBasicAuthHeader() } }
      );
      alert('Organisation settings saved successfully.');
    } catch (err) {
      console.error('Error saving organization settings:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!guardAction('action.settings.profile.save')) return;
    setSaving(true);
    try {
      await axios.post(
        `${apiUrl}/api/users/instructor/${userId}/profile`,
        { firstName, lastName },
        { headers: { Authorization: getBasicAuthHeader() } }
      );
      localStorage.setItem('username', `${firstName} ${lastName}`.trim());
      alert('Profile details saved successfully.');
    } catch (err) {
      console.error('Error saving profile settings:', err);
    } finally {
      setSaving(false);
    }
  };

  const openBillingPortal = async () => {
    if (!guardAction('action.settings.billingPortal')) return;
    try {
      const res = await axios.post(
        `${apiUrl}/api/stripe/portal-session`,
        { userId },
        { headers: { Authorization: getBasicAuthHeader() } },
      );
      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      console.error('Billing portal error:', error);
    }
  };

  return (
    <>
      <DashboardNavbar menuItems={menuItems} />
      <div style={{ maxWidth: '820px', margin: '0 auto', padding: '0 16px' }}>
        <SectionHead style={{ marginTop: 0 }}>
          <div>
            <h2>Settings</h2>
            <p>Manage your workspace, profile and subscription settings.</p>
          </div>
        </SectionHead>

        <SetTabs>
          {visibleTabs.map((tab) => (
            <SetTab key={tab.key} $on={activeTab === tab.key} onClick={() => setTab(tab.key)}>
              {tab.label}
            </SetTab>
          ))}
        </SetTabs>

        {tabLoading ? (
          <Card $pad>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', paddingBottom: '18px', marginBottom: '18px', borderBottom: '1px solid #e5e5ea' }}>
              <Skeleton $circle $h="56px" />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <Skeleton $w="180px" $h="16px" />
                <Skeleton $w="280px" $h="12px" />
              </div>
              <Skeleton $w="80px" $h="30px" />
            </div>
            <FormGrid>
              <div>
                <Skeleton $w="120px" $h="12px" $mb="8px" />
                <Skeleton $w="100%" $h="38px" />
              </div>
              <div>
                <Skeleton $w="120px" $h="12px" $mb="8px" />
                <Skeleton $w="100%" $h="38px" />
              </div>
            </FormGrid>
            <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <Skeleton $w="150px" $h="14px" />
                  <Skeleton $w="240px" $h="11px" />
                </div>
                <Skeleton $w="42px" $h="24px" $r="999px" />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <Skeleton $w="150px" $h="14px" />
                  <Skeleton $w="240px" $h="11px" />
                </div>
                <Skeleton $w="42px" $h="24px" $r="999px" />
              </div>
            </div>
          </Card>
        ) : (
          <>
            {/* 1. Organisation Tab */}
            {activeTab === 'org' && (
              <Card $pad>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', paddingBottom: '18px', borderBottom: '1px solid ${({ theme }) => theme.border}' }}>
                  <LogoPreview>
                    {orgLogo ? <img src={orgLogo} alt="Logo" /> : (orgName ? orgName[0].toUpperCase() : 'S')}
                  </LogoPreview>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: 0, fontSize: '15px' }}>{orgName || 'Workspace logo'}</h4>
                    <p style={{ margin: '3px 0 0', fontSize: '12px', color: '#7e8298' }}>
                      Organisation logo · shown on the sidebar and certificates
                    </p>
                  </div>
                  {canEditOrg && (
                    <Btn as="label" htmlFor="logo-upload" $variant="ghost" style={{ cursor: 'pointer' }}>
                      <Upload size={14} /> Replace
                      <input id="logo-upload" type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
                    </Btn>
                  )}
                </div>

                <div style={{ marginTop: '20px' }}>
                  <FormGrid>
                    <Field>
                      <label>Organisation Name</label>
                      <Input
                        value={orgName}
                        onChange={(e) => setOrgName(e.target.value)}
                        readOnly={!canEditOrg}
                        style={canEditOrg ? undefined : { opacity: 0.7 }}
                      />
                    </Field>
                    <Field>
                      <label>Contact Email</label>
                      <Input value={email || ''} readOnly style={{ opacity: 0.7 }} />
                    </Field>
                  </FormGrid>
                </div>

                <SetRow>
                  <div className="set-row__txt">
                    <h4><LabelWithHelp help={HELP.selfEnrollment}>Allow self-enrollment</LabelWithHelp></h4>
                    <p>Let learners join published courses without an invite link</p>
                  </div>
                  <Switch $on={selfEnroll} onClick={() => {
                    if (!canEditOrg) return;
                    if (!guardAction('action.settings.org.selfEnroll')) return;
                    setSelfEnroll(!selfEnroll);
                  }} />
                </SetRow>

                <SetRow>
                  <div className="set-row__txt">
                    <h4><LabelWithHelp help={HELP.certificates}>Issue certificates on completion</LabelWithHelp></h4>
                    <p>Auto-generate a branded PDF when a course is passed</p>
                  </div>
                  <Switch $on={certificates} onClick={() => {
                    if (!canEditOrg) return;
                    if (!guardAction('action.settings.org.certificates')) return;
                    setCertificates(!certificates);
                  }} />
                </SetRow>

                {canEditOrg ? (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                    <Btn onClick={handleSaveOrg} disabled={saving}>
                      {saving ? 'Saving...' : <><Check size={14} /> Save changes</>}
                    </Btn>
                  </div>
                ) : (
                  <p style={{ marginTop: '20px', fontSize: '12px', color: '#7e8298', fontStyle: 'italic' }}>
                    Only organisation owners and admins can edit these settings.
                  </p>
                )}
              </Card>
            )}

            {/* 2. Profile Tab */}
            {activeTab === 'profile' && (
              <Card $pad>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', paddingBottom: '18px', borderBottom: '1px solid ${({ theme }) => theme.border}' }}>
                  <AvPreview>
                    {firstName ? firstName[0].toUpperCase() : 'U'}
                    {lastName ? lastName[0].toUpperCase() : ''}
                  </AvPreview>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: 0, fontSize: '15px' }}>{firstName} {lastName}</h4>
                    <p style={{ margin: '3px 0 0', fontSize: '12px', color: '#7e8298' }}>
                      Instructor profile · registered under StudYaK workspace
                    </p>
                  </div>
                </div>

                <div style={{ marginTop: '20px' }}>
                  <FormGrid>
                    <Field>
                      <label>First Name</label>
                      <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                    </Field>
                    <Field>
                      <label>Last Name</label>
                      <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
                    </Field>
                  </FormGrid>
                  <Field>
                    <label>Email Address</label>
                    <Input value={profEmail} readOnly style={{ opacity: 0.7 }} />
                  </Field>
                  <Field>
                    <label>Title</label>
                    <Input defaultValue="Instructor" />
                  </Field>
                  <Field>
                    <label>Bio</label>
                    <Textarea defaultValue="StudYaK Educator dedicated to delivering assessments and courses." />
                  </Field>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                  <Btn onClick={handleSaveProfile} disabled={saving}>
                    {saving ? 'Saving...' : <><Check size={14} /> Save changes</>}
                  </Btn>
                </div>
              </Card>
            )}

            {/* 4. Account Tab — payout/bank details were removed on purpose;
                Stripe payouts are managed via the owner's Billing page. */}
            {activeTab === 'account' && (
              <Card $pad>
                {/* Destroying the workspace is org-level — invited instructors never see it. */}
                {!isOrgInstructor(entitlements) && (
                  <SetRow style={{ marginTop: '16px' }}>
                    <div className="set-row__txt">
                      <h4 style={{ color: '#ef4444' }}>Delete workspace</h4>
                      <p>Permanently remove this organization, all connected courses and learner data</p>
                    </div>
                    <Btn $variant="danger" onClick={() => guardAction('action.settings.account.deleteWorkspace')}>
                      <Trash size={14} /> Delete
                    </Btn>
                  </SetRow>
                )}
              </Card>
            )}

            {/* 5. Register Tab — public learner registration settings, inline */}
            {activeTab === 'register' && <RegistrationSettingsCard />}
          </>
        )}
      </div>

      <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </>
  );
}
